import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import type { SetNonNullable } from 'type-fest';
import type { AnnotationVersionChange } from './AnnotationVersionChangeCard';
import AnnotationVersionChangeCard from './AnnotationVersionChangeCard';
import type { ThreadVersionChange } from './ThreadVersionChangeCard';
import ThreadVersionChangeCard from './ThreadVersionChangeCard';
import type {
  MagneticWallFocus,
  MagneticWallHighlight,
} from '../../editor/editorSlice';
import { getClosestAttribute } from '../../editor/formats/utils/attributors';
import { useQuill } from '../../editor/QuillContext';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import annotationThreadHash from '../../utils/url/annotationThreadHash';
import commentThreadHash from '../../utils/url/commentThreadHash';
import { getMagneticWallItemContentRects } from '../MagneticWall';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import useMagneticWallItemSize from '../useMagneticWallItemSize';
import parseMagneticWallHash from '../utils/parseMagneticWallHash';
import useLayoutSize, { LayoutSize } from '../utils/useLayoutSize';

const THREAD_FRAGMENT = gql(`
  fragment NewPostHistoryMagneticWall_thread on CommentThread {
    id
    ...ThreadVersionChangeCard_thread
  }
`);

const ANNOTATION_FRAGMENT = gql(`
  fragment NewPostHistoryMagneticWall_annotation on Annotation {
    id
    ...AnnotationVersionChangeCard_annotation
  }
`);

type NewPostHistoryMagneticWallVersionChanges =
  | ThreadVersionChange<DocumentType<typeof THREAD_FRAGMENT>>
  | AnnotationVersionChange<DocumentType<typeof ANNOTATION_FRAGMENT>>;

interface InnerNewPostHistoryMagneticWallProps {
  versionChanges: NewPostHistoryMagneticWallVersionChanges[];
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  magneticWallItemSize: MagneticWallItemSize;
  canEdit: boolean;
}

const InnerNewPostHistoryMagneticWall = ({
  versionChanges,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  magneticWallItemSize,
  canEdit,
}: InnerNewPostHistoryMagneticWallProps) => {
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();

  const visibleVersionChanges = useMemo(
    () =>
      versionChanges
        .map((item) => ({
          ...item,
          top: layout.tops[item.id],
          isOverlapped: layout.overlappedIds.has(item.id),
        }))
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges],
  );

  const hasVisibleItems = visibleVersionChanges.length > 0;
  const location = useLocation();

  useEffect(() => {
    if (!hasVisibleItems) return;

    const parseResult = parseMagneticWallHash(location.hash);
    if (!parseResult) return;

    setMagneticWallFocus({
      active: true,
      id: parseResult.id,
      type: parseResult.type,
      hasExpandIntent: true,
    });
  }, [hasVisibleItems, location.hash, setMagneticWallFocus]);

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();
  }, [
    visibleVersionChanges,
    debouncedUpdateDesiredPositions,
    updateThreadHeights,
  ]);

  return (
    <ClickOutsideHandler
      onClickOutside={(e) => {
        const { target } = e;

        if (
          target instanceof Element &&
          // clicking on a comment / annotation within the post
          // shouldn't blur the magneticWallFocus, as it's selecting it
          getClosestAttribute(target) != null
        ) {
          return;
        }

        setMagneticWallFocus(null);
      }}
    >
      {visibleVersionChanges.map((versionChange) => {
        const copyLink = (hash: string) => {
          const link = new URL(window.location.href);
          link.hash = hash;
          navigator.clipboard.writeText(link.toString());
        };

        if (versionChange.type === 'annotation') {
          const isFocused = versionChange.id === magneticWallFocus?.id;

          return (
            <MagneticWallLayout.Item
              id={versionChange.id}
              focused={isFocused}
              top={versionChange.top}
              key={versionChange.id}
            >
              <AnnotationVersionChangeCard
                focused={isFocused}
                versionChange={versionChange}
                onFocus={() =>
                  setMagneticWallFocus({
                    id: versionChange.id,
                    type: 'annotation',
                    active: true,
                    hasExpandIntent: false,
                  })
                }
                onCopyLink={() =>
                  copyLink(annotationThreadHash(versionChange.id))
                }
                onRemoveHighlight={() => setMagneticWallHighlight(null)}
                onHighlight={() =>
                  setMagneticWallHighlight({
                    id: versionChange.id,
                    type: 'annotation',
                  })
                }
                magneticWallItemSize={magneticWallItemSize}
              />
            </MagneticWallLayout.Item>
          );
        }

        const isFocused = versionChange.id === magneticWallFocus?.id;
        return (
          <MagneticWallLayout.Item
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            key={versionChange.id}
          >
            <ThreadVersionChangeCard
              focused={isFocused}
              versionChange={versionChange}
              magneticWallItemSize={magneticWallItemSize}
              canEdit={canEdit}
              onFocus={() =>
                setMagneticWallFocus({
                  id: versionChange.id,
                  type: 'comment',
                  active: true,
                  hasExpandIntent: false,
                })
              }
              onCopyLink={() => copyLink(commentThreadHash(versionChange.id))}
              onRemoveHighlight={() => setMagneticWallHighlight(null)}
              onHighlight={() =>
                setMagneticWallHighlight({
                  id: versionChange.id,
                  type: 'comment',
                })
              }
            />
          </MagneticWallLayout.Item>
        );
      })}
    </ClickOutsideHandler>
  );
};

interface NewPostHistoryMagneticWallProps {
  versionChanges: NewPostHistoryMagneticWallVersionChanges[];
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  canEdit: boolean;
}

const NewPostHistoryMagneticWall = ({
  versionChanges,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  canEdit,
}: NewPostHistoryMagneticWallProps) => {
  const layoutSize = useLayoutSize();
  const { quill } = useQuill();
  const containerRef = useRef<HTMLDivElement>(null);
  const magneticWallItemSize = useMagneticWallItemSize(containerRef);

  if (
    layoutSize &&
    [LayoutSize.mobile, LayoutSize.tablet].includes(layoutSize)
  ) {
    return null;
  }

  return (
    <MagneticWallLayout.Root
      containerRef={containerRef}
      itemSize={magneticWallItemSize}
      magneticWallItems={versionChanges}
      focus={magneticWallFocus}
      getRelativeContentRects={() =>
        quill ? getMagneticWallItemContentRects(quill) : []
      }
    >
      <InnerNewPostHistoryMagneticWall
        versionChanges={versionChanges}
        setMagneticWallFocus={setMagneticWallFocus}
        magneticWallFocus={magneticWallFocus}
        setMagneticWallHighlight={setMagneticWallHighlight}
        magneticWallItemSize={magneticWallItemSize}
        canEdit={canEdit}
      />
    </MagneticWallLayout.Root>
  );
};

export default NewPostHistoryMagneticWall;
